<template>
  <div class="photo-action-sheet">
    <!-- Two inputs, not one. `capture="environment"` makes a phone jump STRAIGHT to
         the rear camera with no picker — which is what we want for "Take photo" and
         exactly wrong for "Upload photo", where the same attribute would hide the
         photo library on some Android builds. Desktop browsers ignore `capture`
         entirely, which is why the consumer passes `show-take` only on touch screens.
         Both live OUTSIDE the lazy modal: the modal is torn down while the camera is
         open, and the change event must still have somewhere to land. -->
    <input
      ref="cameraInput"
      type="file"
      accept="image/*"
      capture="environment"
      class="d-none"
      :data-test="`${testId}-camera-input`"
      @change="onFileChosen"
    />
    <input
      ref="fileInput"
      type="file"
      accept="image/*"
      class="d-none"
      :data-test="`${testId}-file-input`"
      @change="onFileChosen"
    />

    <!-- `static` (not the default body-portal) so this component's SCOPED styles
         reach the sheet at all; `lazy` so a page that never opens it pays nothing. -->
    <b-modal
      v-model="open"
      static
      lazy
      hide-header
      hide-footer
      centered
      class="patient-photo-modal-root"
      modal-class="patient-photo-sheet-modal"
      content-class="patient-photo-sheet"
      :data-test="`${testId}-sheet`"
      :no-close-on-backdrop="locked"
      :no-close-on-esc="locked"
    >
      <div class="photo-sheet-title">{{ title }}</div>

      <div class="photo-sheet-scroll">
        <template v-if="pickSource">
          <button
            v-if="showTake"
            type="button"
            class="photo-sheet-row"
            :data-test="`${testId}-take`"
            @click="takePhoto"
          >
            <i class="fa fa-camera"></i><span>Take photo</span>
          </button>

          <button
            type="button"
            class="photo-sheet-row"
            :data-test="`${testId}-upload`"
            @click="uploadPhoto"
          >
            <i class="fa fa-picture-o"></i><span>Upload photo</span>
          </button>
        </template>

        <slot />

        <button
          v-if="showCancel"
          type="button"
          class="photo-sheet-row photo-sheet-cancel"
          :data-test="`${testId}-cancel`"
          :disabled="locked"
          @click="open = false"
        >
          <i class="fa fa-times"></i><span>Cancel</span>
        </button>
      </div>

      <!-- Pinned below the scrolling rows, so a long list can never push the
           consumer's primary action off the bottom of a phone. -->
      <div v-if="$scopedSlots.footer" class="photo-sheet-footer">
        <slot name="footer" />
      </div>
    </b-modal>
  </div>
</template>

<script>
/**
 * The photo action sheet shared by the Patient Profile avatar (PatientPhotoAvatar)
 * and "Photo to OD" (PatientOdImageUpload).
 *
 * WHY IT IS ONE COMPONENT: every rule below was learned the hard way on the avatar
 * (2026-09-11) and none of them is visible in a code review of a second copy —
 * `static` so scoped styles reach the sheet, the z-index lift over the help bot,
 * Bootstrap's centered-modal spacer neutralised for the phone bottom sheet, the
 * two-input camera/library split, the $nextTick input click for iOS, and the
 * `input.value = ''` reset without which the same file picked twice fires nothing.
 *
 * WHAT IT DOES NOT OWN: whether "Take photo" shows (`show-take` — the consumer
 * re-checks hasTouchScreen() when it opens the sheet, because touch emulation can
 * attach after the page starts), the gate, the mask, or what happens to the file.
 * It emits `file` with the picked File and nothing else.
 *
 * Slot content (extra rows, a category list) is rendered by the CONSUMER, so it
 * carries the consumer's scope id — which is why the row styles below are
 * `::v-deep` under this component's own root rather than plain scoped rules.
 */
export default {
  name: 'PhotoActionSheet',

  props: {
    // Open state, for v-model.
    value: { type: Boolean, default: false },
    title: { type: String, default: '' },
    // Prefix for every data-test in the sheet: `${testId}-sheet`, `-take`, `-upload`,
    // `-cancel`, `-camera-input`, `-file-input`. Two sheets live on one page.
    testId: { type: String, default: 'patient-photo' },
    // Render the Take photo / Upload photo rows.
    pickSource: { type: Boolean, default: true },
    showTake: { type: Boolean, default: false },
    showCancel: { type: Boolean, default: true },
    // While true the sheet cannot be dismissed (backdrop, Esc, Cancel) — e.g. mid-send.
    locked: { type: Boolean, default: false },
  },

  computed: {
    open: {
      get() {
        return this.value;
      },
      set(v) {
        if (!v && this.locked) return;
        this.$emit('input', v);
      },
    },
  },

  methods: {
    takePhoto() {
      this.pick('cameraInput');
    },

    uploadPhoto() {
      this.pick('fileInput');
    },

    pick(ref) {
      this.open = false;
      // $nextTick so the click lands after the modal starts closing — iOS Safari
      // drops the file-picker gesture if the element is inside a tree that is
      // being torn down in the same tick.
      this.$nextTick(() => {
        if (this.$refs[ref]) this.$refs[ref].click();
      });
    },

    onFileChosen(e) {
      const input = e.target;
      const file = input.files && input.files[0];
      // Without this, picking the SAME file twice in a row fires no change event.
      // The File object stays valid after the input is cleared.
      try { input.value = ''; } catch (err) { /* some browsers refuse; harmless */ }
      if (file) this.$emit('file', file);
    },
  },
};
</script>

<style lang="scss" scoped>
// ---------------------------------------------------------------------------
// `::v-deep` is kept UNDER .photo-action-sheet so the rules can never leak to
// another page's modal — BootstrapVue renders .modal-dialog / .modal-content
// itself, so they carry BModal's scope id, not ours. This only works because the
// b-modal is `static` (in-tree); a portalled modal would be outside this root.
// ---------------------------------------------------------------------------
.photo-action-sheet {
  // Above the floating help bot (.copilotKitSidebar, z-index 9999) and the SMS widget
  // (1000). Measured on a 390px phone: the bot's bubble sat exactly over the right end of
  // the "Upload photo" row, so a thumb aimed at that row opened the bot instead.
  //
  // The lever is the b-modal's ROOT element, not `.modal`: a `static` BootstrapVue modal
  // renders an unclassed outer <div style="position:absolute; z-index:<stack>"> around the
  // .modal and its backdrop, and that inline z-index is a stacking context of its own — a
  // bigger z-index on .modal inside it changes nothing (measured: .modal at 10050 still
  // sat under the 9999 bubble). `class` on <b-modal> lands on that root; !important because
  // the value is inline. Inside the root, BootstrapVue's own modal/backdrop order stands.
  ::v-deep .patient-photo-modal-root {
    z-index: 10050 !important;
  }

  ::v-deep .patient-photo-sheet-modal {
    .modal-dialog {
      max-width: 380px;
    }

    // Title and footer stay put; only the rows scroll. Bootstrap 4's .modal-content is
    // already a flex column, so capping its height and letting .modal-body shrink is
    // enough for .photo-sheet-scroll to take the overflow.
    .modal-content {
      max-height: calc(100vh - 3.5rem);
      overflow: hidden;
    }

    .modal-body {
      padding: 8px 0 0;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
  }

  ::v-deep .photo-sheet-row {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    min-height: 56px;
    padding: 0 16px;
    border: 0;
    background: transparent;
    font-size: 1rem;
    line-height: 1.3;
    text-align: left;
    color: #1e293b;
    border-top: 1px solid #eef2f7;

    i {
      width: 20px;
      text-align: center;
      color: #0f7259;
      font-size: 1.05rem;
    }

    &.text-danger i {
      color: inherit;
    }

    &:active {
      background: #f1f5f9;
    }

    &:disabled {
      opacity: 0.55;
    }
  }

  ::v-deep .photo-sheet-cancel {
    color: #64748b;

    i {
      color: #94a3b8;
    }
  }
}

.photo-sheet-title {
  flex: 0 0 auto;
  padding: 8px 16px 12px;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.3;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}

.photo-sheet-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 8px;
}

.photo-sheet-footer {
  flex: 0 0 auto;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #e2e8f0;
  // Transparent, not white: the app's global `.modal-body` background is grey
  // (_overrides.scss), and a white footer read as a separate box on the phone sheet.
  background: transparent;
}

@media (max-width: 768px) {
  // Thumb-reachable bottom sheet, the pattern every phone OS already taught staff.
  .photo-action-sheet {
    ::v-deep .patient-photo-sheet-modal {
      .modal-dialog {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        margin: 0;
        max-width: none;
        // The b-modal is `centered`, and Bootstrap 4 implements that with
        // `min-height: calc(100% - 1rem)` on .modal-dialog-centered plus a full-height
        // ::before spacer — either one would push the sheet back to the vertical middle.
        min-height: 0;
        align-items: flex-end;

        &::before {
          display: none;
        }
      }

      .modal-content {
        border-radius: 16px 16px 0 0;
        // vh, not a calc on 100vh: iOS Safari's 100vh includes the collapsed toolbar,
        // so a near-full-height sheet would slide its title under the address bar.
        max-height: 85vh;
      }
    }
  }
}
</style>
